import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, api, ApiError, Button, Card, Input, Select, Spinner } from '@greenrinse/ui';
import { BrandingSection } from '@greenrinse/features';
import { apiBaseUrl, tokenStore } from '@greenrinse/ui';

interface SettingDto { key: string; value: string | null; isSecret: boolean; isSet: boolean; default: string; description: string }

const K = {
  mode: 'PlateRecognition.Mode',
  provider: 'PlateRecognition.Provider',
  apiKey: 'PlateRecognition.ApiKey',
  endpoint: 'PlateRecognition.Endpoint',
  threshold: 'PlateRecognition.ConfidenceThreshold',
  regions: 'PlateRecognition.Regions',
  vcMode: 'VehicleClassification.Mode',
  vcProvider: 'VehicleClassification.Provider',
  vcEndpoint: 'VehicleClassification.Endpoint',
  vcApiKey: 'VehicleClassification.ApiKey',
  vcThreshold: 'VehicleClassification.ConfidenceThreshold',
  vcAutoFill: 'VehicleClassification.AutoFillUnlisted',
  pqChecks: 'PhotoQuality.Checks',
  pqMinBrightness: 'PhotoQuality.MinBrightness',
  pqMaxBrightness: 'PhotoQuality.MaxBrightness',
  pqMinSharpness: 'PhotoQuality.MinSharpness',
  clMode: 'Cleanliness.Mode',
  clProvider: 'Cleanliness.Provider',
  clApiKey: 'Cleanliness.ApiKey',
  clModel: 'Cleanliness.Model',
  clEndpoint: 'Cleanliness.Endpoint',
  clThreshold: 'Cleanliness.Threshold',
  dmMode: 'Damage.Mode',
  dmMinConfidence: 'Damage.MinConfidence',
  dmEndpoint: 'Damage.Endpoint',
  retention: 'Photos.RetentionDays',
  maxEdge: 'Photos.MaxLongEdgePx',
  watermark: 'Photos.WatermarkEnabled',
  minDuration: 'Verification.MinDurationSeconds',
  maxDrift: 'Verification.MaxTimeDriftSeconds',
  geoTol: 'Verification.GeofenceToleranceMeters',
  storageLow: 'Storage.LowWarnGb',
  emailEnabled: 'Notifications.Email.Enabled',
  emailProvider: 'Notifications.Email.Provider',
  fromAddress: 'Notifications.Email.FromAddress',
  fromName: 'Notifications.Email.FromName',
  smtpHost: 'Notifications.Smtp.Host',
  smtpPort: 'Notifications.Smtp.Port',
  smtpUser: 'Notifications.Smtp.User',
  smtpPassword: 'Notifications.Smtp.Password',
  smtpSsl: 'Notifications.Smtp.UseSsl',
  sendGridKey: 'Notifications.SendGrid.ApiKey',
  pushEnabled: 'Notifications.Push.Enabled',
  vapidPublic: 'Notifications.Push.VapidPublicKey',
  pushSubject: 'Notifications.Push.Subject',
  adminUrl: 'Notifications.AdminUrl',
  partnerUrl: 'Notifications.PartnerUrl',
  apiUrl: 'Notifications.ApiUrl',
  esgBaseline: 'Sustainability.BaselineLitresPerWash',
  esgDefault: 'Sustainability.DefaultLitresPerWash',
  esgKm: 'Sustainability.AvoidedKmPerWash',
  esgCo2: 'Sustainability.Co2GramsPerKm',
  payProvider: 'Payments.Provider',
  stripePk: 'Payments.Stripe.PublishableKey',
  stripeSk: 'Payments.Stripe.SecretKey',
  stripeWh: 'Payments.Stripe.WebhookSecret',
  smsEnabled: 'Notifications.Sms.Enabled',
  smsSid: 'Notifications.Sms.AccountSid',
  smsToken: 'Notifications.Sms.AuthToken',
  smsFrom: 'Notifications.Sms.FromNumber',
  esgPublic: 'Sustainability.PublicCountersEnabled',
  slaWarnDays: 'Sla.WarnDaysBeforeCycleEnd',
  slaGrace: 'Sla.VisitMissedGraceHours',
} as const;

/**
 * Admin → Settings. The important switch is PlateRecognition.Mode:
 *   Manual → washer types the plate, no AI calls.
 *   AI     → the configured provider reads the plate from the photo and pre-fills it.
 */
interface PhotoCheck {
  metrics: { brightness: number; sharpness: number; width: number; height: number };
  quality: { ok: boolean; problems: string[] };
  ai: { configured: boolean; provider?: string; model?: string; ok?: boolean; message?: string; threshold?: number; wouldFlag?: boolean;
        result?: { score: number; verdict: string; issues: string[] } };
}

/** Upload one photo and see what the saved settings make of it (nothing is stored). */
function PhotoCheckTester() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<PhotoCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true); setError(null); setRes(null);
    const form = new FormData();
    form.append('file', file);
    try { setRes(await api<PhotoCheck>('/api/settings/photo-check', { method: 'POST', form })); }
    catch (e) { setError(e instanceof ApiError ? e.message : t('common.error_generic')); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--gr-outline-variant, #DCE8DF)' }}>
      <strong>{t('settings.cl_test')}</strong>
      <p className="gr-muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>{t('settings.cl_test_hint')}</p>
      <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(e) => void run(e.target.files?.[0])} />
      {busy && <Spinner />}
      {error && <Alert tone="error">{error}</Alert>}
      {res && (
        <div className="gr-stack" style={{ marginTop: 8, gap: 8 }}>
          <Alert tone={res.quality.ok ? 'success' : 'error'}>
            {t('settings.cl_builtin')}: {res.quality.ok ? t('settings.cl_ok') : res.quality.problems.join(', ')}
            {' '}<small>({t('settings.cl_brightness')} {Math.round(res.metrics.brightness)}, {t('settings.cl_sharpness')} {Math.round(res.metrics.sharpness)}, {res.metrics.width}×{res.metrics.height})</small>
          </Alert>
          {!res.ai.configured ? <Alert tone="info">{t('settings.cl_not_configured')}</Alert>
            : res.ai.ok && res.ai.result ? (
              <Alert tone={res.ai.wouldFlag ? 'error' : 'success'}>
                AI ({res.ai.provider}{res.ai.model && res.ai.provider === 'Claude' ? ` · ${res.ai.model}` : ''}): <strong>{res.ai.result.score}/100 · {res.ai.result.verdict}</strong>
                {res.ai.result.issues.length > 0 ? ` — ${res.ai.result.issues.join(', ')}` : ''}
                {' · '}{res.ai.wouldFlag ? t('settings.cl_would_flag', { threshold: res.ai.threshold }) : t('settings.cl_would_pass', { threshold: res.ai.threshold })}
              </Alert>
            ) : <Alert tone="error">AI ({res.ai.provider}): {res.ai.message}</Alert>}
        </div>
      )}
    </div>
  );
}

interface DamageCheck {
  configured: boolean; provider?: string; model?: string; ok?: boolean; message?: string; minConfidence?: number; wouldFlag?: boolean;
  findings?: { location: string; type: string; severity: string; timing: string; confidence: number; description: string }[];
}

/** Upload before + after photos and see what AI damage detection finds with the saved settings (nothing is stored). */
function DamageCheckTester() {
  const { t } = useTranslation();
  const [before, setBefore] = useState<File[]>([]);
  const [after, setAfter] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<DamageCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setError(null); setRes(null);
    const form = new FormData();
    before.slice(0, 2).forEach((f) => form.append('before', f));
    after.slice(0, 2).forEach((f) => form.append('after', f));
    try { setRes(await api<DamageCheck>('/api/settings/damage-check', { method: 'POST', form })); }
    catch (e) { setError(e instanceof ApiError ? e.message : t('common.error_generic')); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--gr-outline-variant, #DCE8DF)' }}>
      <strong>{t('settings.dm_test')}</strong>
      <p className="gr-muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>{t('settings.cl_test_hint')}</p>
      <div className="gr-form-grid">
        <label>{t('settings.dm_before')}<br /><input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={(e) => setBefore(Array.from(e.target.files ?? []))} /></label>
        <label>{t('settings.dm_after')}<br /><input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={(e) => setAfter(Array.from(e.target.files ?? []))} /></label>
      </div>
      <Button type="button" variant="tonal" loading={busy} disabled={before.length === 0 || after.length === 0} onClick={() => void run()} style={{ marginTop: 8 }}>{t('settings.dm_run')}</Button>
      {error && <Alert tone="error">{error}</Alert>}
      {res && (!res.configured ? <Alert tone="info">{t('settings.dm_not_configured')}</Alert>
        : !res.ok ? <Alert tone="error">AI ({res.provider}): {res.message}</Alert>
        : (
          <div style={{ marginTop: 8 }}>
            <Alert tone={res.wouldFlag ? 'error' : 'success'}>
              {res.findings!.length === 0 ? t('settings.dm_none') : t('settings.dm_found', { count: res.findings!.length })}
              {' · '}{res.wouldFlag ? t('settings.dm_would_flag') : t('settings.dm_would_pass')}
            </Alert>
            {res.findings!.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingInlineStart: 18 }}>
                {res.findings!.map((f, i) => (
                  <li key={i} style={{ opacity: f.confidence < (res.minConfidence ?? 0) ? 0.55 : 1 }}>
                    <strong>{f.location}</strong>: {f.description} — {f.type.replace('_', ' ')}, {f.severity}, {f.timing}, {f.confidence}%
                    {f.confidence < (res.minConfidence ?? 0) ? ` (${t('settings.dm_below_conf')})` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
    </div>
  );
}

/** Backup download + data clean-up. Rendered outside the settings <form>: its buttons must not submit it. */
function MaintenanceSection() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const fail = (e: unknown) => setMsg({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') });

  const download = async () => {
    setBusy(true); setMsg({ tone: 'info', text: t('settings.backup_running') });
    try {
      const res = await fetch(`${apiBaseUrl}/api/maintenance/backup`, { method: 'POST', headers: { Authorization: `Bearer ${tokenStore.get()?.accessToken ?? ''}` } });
      if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => null)) ?? { code: 'error', message: t('common.error_generic') });
      const name = res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'GreenRinse.bak';
      const a = document.createElement('a'); a.href = URL.createObjectURL(await res.blob()); a.download = name; a.click(); URL.revokeObjectURL(a.href);
      setMsg({ tone: 'success', text: t('settings.backup_done', { name }) });
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const clean = async () => {
    if (!window.confirm(t('settings.clean_confirm'))) return;
    setBusy(true); setMsg(null);
    try {
      const counts = await api<Record<string, number>>('/api/maintenance/clean', { method: 'POST', body: { password } });
      setPassword('');
      setMsg({ tone: 'success', text: t('settings.clean_done', { count: Object.values(counts).reduce((a, b) => a + b, 0) }) });
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  return (
    <div className="gr-stack" style={{ maxWidth: 720 }}>
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <Card>
        <h2>💾 {t('settings.backup_title')}</h2>
        <p className="gr-muted" style={{ marginTop: 0, fontSize: 13 }}>{t('settings.backup_hint')}</p>
        <Button type="button" variant="outline" loading={busy} onClick={() => void download()}>{t('settings.backup_download')}</Button>
      </Card>
      <Card>
        <h2>🧹 {t('settings.clean_title')}</h2>
        <p className="gr-muted" style={{ marginTop: 0, fontSize: 13 }}>{t('settings.clean_hint')}</p>
        <Input label={t('settings.clean_password')} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="button" variant="outline" loading={busy} disabled={!password} onClick={() => void clean()} style={{ color: 'var(--gr-error, #b3261e)' }}>{t('settings.clean_run')}</Button>
      </Card>
    </div>
  );
}

const TABS = ['company', 'ai', 'photos', 'notifications', 'payments', 'backup'] as const;

export default function SettingsPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<SettingDto[] | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [vapidBusy, setVapidBusy] = useState(false);
  const [tab, setTab] = useState<(typeof TABS)[number]>('company');
  const [testBusy, setTestBusy] = useState(false);
  const [smsTo, setSmsTo] = useState('');

  useEffect(() => {
    api<SettingDto[]>('/api/settings').then((list) => {
      setSettings(list);
      setForm(Object.fromEntries(list.map((s) => [s.key, s.value ?? ''])));
    }).catch((e) => setMessage({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') }));
  }, [t]);

  const set = (key: string) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? String((e.target as HTMLInputElement).checked) : e.target.value }));

  const aiOn = form[K.mode] === 'AI';
  const apiKeyState = useMemo(() => settings?.find((s) => s.key === K.apiKey), [settings]);
  const aiWithoutKey = aiOn && !apiKeyState?.isSet && !form[K.apiKey];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true); setMessage(null); setFieldErrors({});
    try {
      await api('/api/settings', { method: 'PUT', body: { values: form } });
      const list = await api<SettingDto[]>('/api/settings');
      setSettings(list);
      setForm(Object.fromEntries(list.map((s) => [s.key, s.value ?? ''])));
      setMessage({ tone: 'success', text: t('settings.saved') });
    } catch (err) {
      if (err instanceof ApiError) { setMessage({ tone: 'error', text: err.message }); setFieldErrors(err.fieldErrors); }
      else setMessage({ tone: 'error', text: t('common.error_generic') });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <Spinner label={t('common.loading')} />;

  const err = (key: string) => fieldErrors[key]?.[0] ?? fieldErrors['values']?.[0];
  const isSet = (key: string) => settings.find((s) => s.key === key)?.isSet;
  const emailOn = form[K.emailEnabled] === 'true';
  const sendGrid = form[K.emailProvider] === 'SendGrid';

  const runTest = async (path: string, body: object | undefined, done: (r: never) => string) => {
    setTestBusy(true); setMessage(null);
    try { setMessage({ tone: 'success', text: done(await api(path, { method: 'POST', body })) }); }
    catch (e) { setMessage({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') }); }
    finally { setTestBusy(false); }
  };
  const stripeOn = form[K.payProvider] === 'Stripe';
  const smsOn = form[K.smsEnabled] === 'true';

  const generateVapid = async () => {
    if (!window.confirm(t('notifications.vapid_warning'))) return;
    setVapidBusy(true); setMessage(null);
    try {
      const r = await api<{ publicKey: string }>('/api/notifications/push/generate-vapid', { method: 'POST' });
      setForm((f) => ({ ...f, [K.vapidPublic]: r.publicKey, [K.pushEnabled]: 'true' }));
      setSettings(await api<SettingDto[]>('/api/settings'));
      setMessage({ tone: 'success', text: t('notifications.vapid_done') });
    } catch (e) { setMessage({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') }); }
    finally { setVapidBusy(false); }
  };

  return (
    <>
    <form onSubmit={submit} className="gr-stack" style={{ maxWidth: 720 }}>
      <h1>{t('settings.title')}</h1>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}

      <nav className="gr-tabs" aria-label={t('settings.title')}>
        {TABS.map((id) => <button key={id} type="button" data-tab={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{t(`settings.tab_${id}`)}</button>)}
      </nav>

      {tab === 'company' && (<>
      <BrandingSection />

      <Card>
        <h2>🌿 {t('settings.esg_title')}</h2>
        <div className="gr-form-grid">
          <Input label={t('settings.esg_baseline')} type="number" min={1} value={form[K.esgBaseline] ?? '150'} onChange={set(K.esgBaseline)} />
          <Input label={t('settings.esg_default')} type="number" min={0} step="0.1" value={form[K.esgDefault] ?? '1'} onChange={set(K.esgDefault)} />
          <Input label={t('settings.esg_km')} type="number" min={0} step="0.5" value={form[K.esgKm] ?? '8'} onChange={set(K.esgKm)} />
          <Input label={t('settings.esg_co2')} type="number" min={0} value={form[K.esgCo2] ?? '170'} onChange={set(K.esgCo2)} />
        </div>
        <label className="gr-row" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={form[K.esgPublic] === 'true'} onChange={set(K.esgPublic)} /> {t('settings.esg_public')}
        </label>
        <p className="gr-muted" style={{ fontSize: 13 }}>{t('settings.esg_public_hint')}</p>
      </Card>

      <Card>
        <h2>📅 {t('settings.sla_title')}</h2>
        <div className="gr-form-grid">
          <Input label={t('settings.sla_warn_days')} type="number" min={0} max={30} value={form[K.slaWarnDays] ?? '3'} onChange={set(K.slaWarnDays)} />
          <Input label={t('settings.sla_grace')} type="number" min={0} max={48} value={form[K.slaGrace] ?? '6'} onChange={set(K.slaGrace)} />
        </div>
      </Card>
      </>)}

      {tab === 'ai' && (<>
      <Card>
        <h2>{t('settings.plate_recognition')}</h2>
        <Select label={t('settings.plate_mode')} value={form[K.mode] ?? 'Manual'} onChange={set(K.mode)} error={err(K.mode)}
          options={[{ value: 'Manual', label: t('settings.mode_manual') }, { value: 'AI', label: t('settings.mode_ai') }]} />
        {aiWithoutKey && <div style={{ marginBottom: 16 }}><Alert tone="info">{t('settings.ai_needs_key')}</Alert></div>}
        <div style={{ opacity: aiOn ? 1 : 0.55 }}>
          <Select label={t('settings.provider')} value={form[K.provider] ?? 'PlateRecognizer'} onChange={set(K.provider)} disabled={!aiOn}
            options={[{ value: 'PlateRecognizer', label: 'Plate Recognizer (platerecognizer.com)' }]} />
          <Input label={t('settings.api_key')} hint={t('settings.api_key_hint')} type="password" autoComplete="off" disabled={!aiOn}
            value={form[K.apiKey] ?? ''} onChange={set(K.apiKey)} placeholder={apiKeyState?.isSet ? '••••••••' : ''} />
          <Input label={t('settings.endpoint')} disabled={!aiOn} value={form[K.endpoint] ?? ''} onChange={set(K.endpoint)} />
          <Input label={t('settings.threshold')} hint={t('settings.threshold_hint')} type="number" min={0} max={100} disabled={!aiOn}
            value={form[K.threshold] ?? '90'} onChange={set(K.threshold)} error={err(K.threshold)} />
          <Input label={t('settings.regions')} hint={t('settings.regions_hint')} disabled={!aiOn} value={form[K.regions] ?? ''} onChange={set(K.regions)} />
        </div>
      </Card>

      <Card>
        <h2>{t('settings.vc_title')}</h2>
        <Select label={t('settings.vc_mode')} value={form[K.vcMode] ?? 'Off'} onChange={set(K.vcMode)}
          options={[{ value: 'Off', label: t('settings.vc_off') }, { value: 'AI', label: t('settings.vc_ai') }]} />
        <div style={{ opacity: form[K.vcMode] === 'AI' ? 1 : 0.55 }}>
          <Select label={t('settings.provider')} value={form[K.vcProvider] ?? 'PlateRecognizer'} onChange={set(K.vcProvider)} disabled={form[K.vcMode] !== 'AI'}
            options={[{ value: 'PlateRecognizer', label: 'Plate Recognizer (same key as plate reading)' }, { value: 'Custom', label: t('settings.vc_custom') }]} />
          {form[K.vcProvider] === 'Custom' && <>
            <Input label={t('settings.endpoint')} hint={t('settings.vc_custom_hint')} disabled={form[K.vcMode] !== 'AI'} value={form[K.vcEndpoint] ?? ''} onChange={set(K.vcEndpoint)} />
            <Input label={t('settings.api_key')} type="password" autoComplete="off" disabled={form[K.vcMode] !== 'AI'} value={form[K.vcApiKey] ?? ''} onChange={set(K.vcApiKey)} placeholder={settings.find((s) => s.key === K.vcApiKey)?.isSet ? '••••••••' : ''} />
          </>}
          <Input label={t('settings.vc_threshold')} hint={t('settings.vc_threshold_hint')} type="number" min={0} max={100} disabled={form[K.vcMode] !== 'AI'} value={form[K.vcThreshold] ?? '70'} onChange={set(K.vcThreshold)} />
          <label className="gr-row" style={{ marginBottom: 8 }}>
            <input type="checkbox" disabled={form[K.vcMode] !== 'AI'} checked={form[K.vcAutoFill] !== 'false'} onChange={set(K.vcAutoFill)} /> {t('settings.vc_autofill')}
          </label>
        </div>
      </Card>

      <Card>
        <h2>{t('settings.pq_title')}</h2>
        <label className="gr-row" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={form[K.pqChecks] !== 'false'} onChange={set(K.pqChecks)} /> {t('settings.pq_checks')}
        </label>
        <p className="gr-muted" style={{ marginTop: 0, fontSize: 13 }}>{t('settings.pq_checks_hint')}</p>
        <div className="gr-form-grid" style={{ opacity: form[K.pqChecks] !== 'false' ? 1 : 0.55 }}>
          <Input label={t('settings.pq_min_brightness')} type="number" min={0} max={255} value={form[K.pqMinBrightness] ?? '40'} onChange={set(K.pqMinBrightness)} disabled={form[K.pqChecks] === 'false'} />
          <Input label={t('settings.pq_max_brightness')} type="number" min={0} max={255} value={form[K.pqMaxBrightness] ?? '235'} onChange={set(K.pqMaxBrightness)} disabled={form[K.pqChecks] === 'false'} />
          <Input label={t('settings.pq_min_sharpness')} hint={t('settings.pq_min_sharpness_hint')} type="number" min={0} value={form[K.pqMinSharpness] ?? '40'} onChange={set(K.pqMinSharpness)} disabled={form[K.pqChecks] === 'false'} />
        </div>

        <h3 style={{ marginBottom: 4 }}>{t('settings.cl_title')}</h3>
        <Select label={t('settings.cl_mode')} value={form[K.clMode] ?? 'Off'} onChange={set(K.clMode)}
          options={[{ value: 'Off', label: t('settings.cl_off') }, { value: 'AI', label: t('settings.cl_ai') }]} />
        <div style={{ opacity: form[K.clMode] === 'AI' ? 1 : 0.55 }}>
          <Select label={t('settings.provider')} value={form[K.clProvider] ?? 'Claude'} onChange={set(K.clProvider)} disabled={form[K.clMode] !== 'AI'}
            options={[{ value: 'Claude', label: t('settings.cl_claude') }, { value: 'Custom', label: t('settings.cl_custom') }]} />
          {form[K.clProvider] === 'Custom'
            ? <Input label={t('settings.endpoint')} hint={t('settings.cl_custom_hint')} disabled={form[K.clMode] !== 'AI'} value={form[K.clEndpoint] ?? ''} onChange={set(K.clEndpoint)} />
            : <Input label={t('settings.cl_model')} hint={t('settings.cl_model_hint')} disabled={form[K.clMode] !== 'AI'} value={form[K.clModel] ?? 'claude-opus-5-5'} onChange={set(K.clModel)} />}
          <Input label={t('settings.api_key')} hint={form[K.clProvider] === 'Custom' ? undefined : t('settings.cl_key_hint')} type="password" autoComplete="off" disabled={form[K.clMode] !== 'AI'}
            value={form[K.clApiKey] ?? ''} onChange={set(K.clApiKey)} placeholder={isSet(K.clApiKey) ? '••••••••' : ''} />
          {form[K.clMode] === 'AI' && !isSet(K.clApiKey) && !form[K.clApiKey] && form[K.clProvider] !== 'Custom' && <Alert tone="info">{t('settings.cl_no_key')}</Alert>}
          <Input label={t('settings.cl_threshold')} hint={t('settings.cl_threshold_hint')} type="number" min={0} max={100} disabled={form[K.clMode] !== 'AI'} value={form[K.clThreshold] ?? '60'} onChange={set(K.clThreshold)} />
        </div>
        <PhotoCheckTester />

        <h3 style={{ marginBottom: 4 }}>{t('settings.dm_title')}</h3>
        <p className="gr-muted" style={{ marginTop: 0, fontSize: 13 }}>{t('settings.dm_hint')}</p>
        <Select label={t('settings.dm_mode')} value={form[K.dmMode] ?? 'Off'} onChange={set(K.dmMode)}
          options={[{ value: 'Off', label: t('settings.cl_off_plain') }, { value: 'AI', label: t('settings.dm_ai') }]} />
        <div style={{ opacity: form[K.dmMode] === 'AI' ? 1 : 0.55 }}>
          <Input label={t('settings.dm_min_conf')} hint={t('settings.dm_min_conf_hint')} type="number" min={0} max={100} disabled={form[K.dmMode] !== 'AI'} value={form[K.dmMinConfidence] ?? '70'} onChange={set(K.dmMinConfidence)} />
          {form[K.clProvider] === 'Custom' && <Input label={t('settings.dm_endpoint')} hint={t('settings.dm_endpoint_hint')} disabled={form[K.dmMode] !== 'AI'} value={form[K.dmEndpoint] ?? ''} onChange={set(K.dmEndpoint)} />}
          {form[K.dmMode] === 'AI' && !isSet(K.clApiKey) && !form[K.clApiKey] && form[K.clProvider] !== 'Custom' && <Alert tone="info">{t('settings.cl_no_key')}</Alert>}
        </div>
        <DamageCheckTester />
      </Card>
      </>)}

      {tab === 'photos' && (<>
      <Card>
        <h2>{t('settings.photos')}</h2>
        <Input label={t('settings.retention_days')} type="number" min={30} value={form[K.retention] ?? ''} onChange={set(K.retention)} />
        <Input label={t('settings.max_edge')} type="number" min={800} max={4000} value={form[K.maxEdge] ?? ''} onChange={set(K.maxEdge)} />
        <label className="gr-row" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={form[K.watermark] === 'true'} onChange={set(K.watermark)} /> {t('settings.watermark')}
        </label>
      </Card>

      <Card>
        <h2>{t('settings.verification')}</h2>
        <Input label={t('settings.min_duration')} type="number" min={0} value={form[K.minDuration] ?? ''} onChange={set(K.minDuration)} />
        <Input label={t('settings.max_drift')} type="number" min={0} value={form[K.maxDrift] ?? ''} onChange={set(K.maxDrift)} />
        <Input label={t('settings.geofence_tolerance')} type="number" min={0} value={form[K.geoTol] ?? ''} onChange={set(K.geoTol)} />
      </Card>
      </>)}

      {tab === 'notifications' && (<>
      <Card>
        <h2>{t('notifications.settings_title')}</h2>
        <Input label={t('settings.admin_url')} hint={t('settings.urls_hint')} value={form[K.adminUrl] ?? ''} onChange={set(K.adminUrl)} />
        <Input label={t('settings.partner_url')} value={form[K.partnerUrl] ?? ''} onChange={set(K.partnerUrl)} />
        <Input label={t('settings.api_url')} hint={t('settings.api_url_hint')} value={form[K.apiUrl] ?? ''} onChange={set(K.apiUrl)} />
        <Input label={t('settings.storage_low')} type="number" min={1} value={form[K.storageLow] ?? '5'} onChange={set(K.storageLow)} />

        <h3 style={{ marginTop: 16 }}>{t('notifications.email_section')}</h3>
        <label className="gr-row" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={emailOn} onChange={set(K.emailEnabled)} /> {t('settings.email_enabled')}
        </label>
        <div style={{ opacity: emailOn ? 1 : 0.55 }}>
          <Select label={t('settings.email_provider')} value={form[K.emailProvider] ?? 'Smtp'} onChange={set(K.emailProvider)} disabled={!emailOn}
            options={[{ value: 'Smtp', label: 'SMTP' }, { value: 'SendGrid', label: 'SendGrid API' }]} />
          <div className="gr-form-grid">
            <Input label={t('settings.from_address')} type="email" disabled={!emailOn} value={form[K.fromAddress] ?? ''} onChange={set(K.fromAddress)} />
            <Input label={t('settings.from_name')} disabled={!emailOn} value={form[K.fromName] ?? ''} onChange={set(K.fromName)} />
          </div>
          {sendGrid ? (
            <Input label={t('settings.sendgrid_key')} type="password" autoComplete="off" disabled={!emailOn} value={form[K.sendGridKey] ?? ''} onChange={set(K.sendGridKey)} placeholder={isSet(K.sendGridKey) ? '••••••••' : ''} />
          ) : (
            <>
              <div className="gr-form-grid">
                <Input label={t('settings.smtp_host')} disabled={!emailOn} value={form[K.smtpHost] ?? ''} onChange={set(K.smtpHost)} />
                <Input label={t('settings.smtp_port')} type="number" min={1} max={65535} disabled={!emailOn} value={form[K.smtpPort] ?? '587'} onChange={set(K.smtpPort)} />
                <Input label={t('settings.smtp_user')} autoComplete="off" disabled={!emailOn} value={form[K.smtpUser] ?? ''} onChange={set(K.smtpUser)} />
                <Input label={t('settings.smtp_password')} type="password" autoComplete="new-password" disabled={!emailOn} value={form[K.smtpPassword] ?? ''} onChange={set(K.smtpPassword)} placeholder={isSet(K.smtpPassword) ? '••••••••' : ''} />
              </div>
              <label className="gr-row" style={{ marginBottom: 8 }}>
                <input type="checkbox" disabled={!emailOn} checked={form[K.smtpSsl] !== 'false'} onChange={set(K.smtpSsl)} /> {t('settings.smtp_ssl')}
              </label>
            </>
          )}
        </div>

        <h3 style={{ marginTop: 16 }}>{t('notifications.push_section')}</h3>
        <label className="gr-row" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={form[K.pushEnabled] === 'true'} onChange={set(K.pushEnabled)} /> {t('settings.push_enabled')}
        </label>
        <Input label={t('settings.vapid_public')} readOnly value={form[K.vapidPublic] ?? ''} hint={form[K.vapidPublic] ? undefined : t('settings.vapid_missing')} />
        <Input label={t('settings.push_subject')} hint={t('settings.push_subject_hint')} value={form[K.pushSubject] ?? ''} onChange={set(K.pushSubject)} />
        <Button type="button" variant="outline" loading={vapidBusy} onClick={() => void generateVapid()}>{t('notifications.generate_vapid')}</Button>
        <p className="gr-muted" style={{ fontSize: 13, marginTop: 8 }}>{t('settings.push_test_hint')}</p>
      </Card>

      <Card>
        <h2>💬 {t('settings.sms_title')}</h2>
        <label className="gr-row" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={smsOn} onChange={set(K.smsEnabled)} /> {t('settings.sms_enabled')}
        </label>
        {smsOn && (
          <>
            <div className="gr-form-grid">
              <Input label={t('settings.sms_sid')} autoComplete="off" value={form[K.smsSid] ?? ''} onChange={set(K.smsSid)} placeholder="AC…" />
              <Input label={t('settings.sms_token')} hint={t('settings.api_key_hint')} type="password" autoComplete="off" value={form[K.smsToken] ?? ''} onChange={set(K.smsToken)} placeholder={isSet(K.smsToken) ? '••••••••' : ''} />
            </div>
            <Input label={t('settings.sms_from')} hint={t('settings.sms_from_hint')} value={form[K.smsFrom] ?? ''} onChange={set(K.smsFrom)} placeholder="+971500000000" />
            <div className="gr-form-grid">
              <Input label={t('settings.sms_test_to')} type="tel" value={smsTo} onChange={(e) => setSmsTo(e.target.value)} placeholder="+971…" />
            </div>
            <Button type="button" variant="outline" loading={testBusy} disabled={!smsTo || !isSet(K.smsToken)}
              onClick={() => void runTest('/api/maintenance/sms/test', { to: smsTo }, () => t('settings.sms_sent', { to: smsTo }))}>
              {t('settings.sms_test')}
            </Button>
            <p className="gr-muted" style={{ fontSize: 13, marginTop: 8 }}>{t('settings.test_save_first')}</p>
          </>
        )}
      </Card>
      </>)}

      {tab === 'payments' && (<>
      <Card>
        <h2>💳 {t('settings.pay_title')}</h2>
        <Select label={t('settings.pay_provider')} value={form[K.payProvider] ?? 'Off'} onChange={set(K.payProvider)}
          options={[{ value: 'Off', label: t('settings.pay_off') }, { value: 'Stripe', label: 'Stripe' }]} />
        {stripeOn && (
          <>
            <Input label={t('settings.stripe_pk')} autoComplete="off" value={form[K.stripePk] ?? ''} onChange={set(K.stripePk)} placeholder="pk_live_…" />
            <Input label={t('settings.stripe_sk')} hint={t('settings.api_key_hint')} type="password" autoComplete="off" value={form[K.stripeSk] ?? ''} onChange={set(K.stripeSk)} placeholder={isSet(K.stripeSk) ? '••••••••' : 'sk_live_…'} />
            <Input label={t('settings.stripe_wh')} type="password" autoComplete="off" value={form[K.stripeWh] ?? ''} onChange={set(K.stripeWh)} placeholder={isSet(K.stripeWh) ? '••••••••' : 'whsec_…'} />
            <Button type="button" variant="outline" loading={testBusy} disabled={!isSet(K.stripeSk)}
              onClick={() => void runTest('/api/maintenance/stripe/test', undefined, (r: { liveMode: boolean; currencies: string[] }) => t('settings.stripe_ok', { mode: r.liveMode ? 'live' : 'test', currencies: r.currencies.join(', ') || '—' }))}>
              {t('settings.stripe_test')}
            </Button>
            <p className="gr-muted" style={{ fontSize: 13, marginTop: 8 }}>{t('settings.test_save_first')}</p>
          </>
        )}
      </Card>
      </>)}

      {tab !== 'backup' && (
        <div className="gr-row">
          <Button type="submit" size="lg" loading={saving}>{t('common.save')}</Button>
        </div>
      )}
    </form>
    {tab === 'backup' && <MaintenanceSection />}
    </>
  );
}
